import { useState } from 'react'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { MODE_LABEL, SUPPLIER_MODES } from '@/components/features/planning/sidebar/supplier-orders-helpers'
import {
  addSupplierBrand,
  addSupplierEmailAddress,
  removeSupplierBrand,
  removeSupplierEmailAddress,
  updateSupplier,
  type BrandOverviewDto,
  type OrderPlatformAddressDto,
  type SupplierOrderMode,
  type SupplierOverviewDto,
} from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import { findAddressOwner, isOwnBrand } from '@/lib/supplierDirectory'
import { cn } from '@/lib/utils'

const settingsSchema = z
  .object({
    name: z.string().trim().min(1, 'Le nom est requis.').max(200, '200 caractères au plus.'),
    allowedModes: z.array(z.enum(['Email', 'Portal', 'Tradeplace', 'Phone'])).min(1, 'Choisissez au moins un mode de commande.'),
    expectsConfirmation: z.boolean(),
    defaultConfirmationDelayDays: z.number().int().min(1, 'Au moins 1 jour.').max(60, '60 jours au plus.').nullable(),
  })
  .strict()

const SOURCE_LABEL = { Manual: 'ajoutée à la main', EmailImport: 'trouvée par import' } as const

interface SupplierSheetProps {
  supplier: SupplierOverviewDto | null
  suppliers: SupplierOverviewDto[]
  platforms: OrderPlatformAddressDto[]
  brands: BrandOverviewDto[]
  canManage: boolean
  manageHint?: string
  onClose: () => void
  /** Reloads the page's directory; awaited where a form must stay locked until it lands. */
  onChanged: () => void | Promise<void>
}

/** The supplier card (01-page-fournisseurs.md § P4): settings, brands, every address. */
export function SupplierSheet({ supplier, suppliers, platforms, brands, canManage, manageHint, onClose, onChanged }: SupplierSheetProps) {
  // While its settings are saved (and reloaded) the card stays open: closed and reopened meanwhile,
  // a fresh form would accept edits the landing save then wipes.
  const [settingsSaving, setSettingsSaving] = useState(false)
  return (
    <Sheet open={supplier !== null} onOpenChange={(open) => !open && !settingsSaving && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="supplier-sheet">
        {supplier && (
          <>
            <SheetHeader>
              <SheetTitle>{supplier.name}</SheetTitle>
              <SheetDescription>
                {canManage ? 'Réglages de commande, marques fournies et adresses email.' : 'Lecture seule.'}
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-6 px-4 pb-6">
              {/* Keyed by the saved settings too: a save that lands after the card was reopened refreshes
                  the form instead of leaving the old values there, dirty, ready to undo the save. */}
              <SettingsSection
                key={settingsKey(supplier)}
                supplier={supplier}
                canManage={canManage}
                manageHint={manageHint}
                onChanged={onChanged}
                onSavingChange={setSettingsSaving}
              />
              <BrandsSection key={`brands-${supplier.id}`} supplier={supplier} brands={brands} canManage={canManage} onChanged={onChanged} />
              <AddressesSection
                key={`addresses-${supplier.id}`}
                supplier={supplier}
                suppliers={suppliers}
                platforms={platforms}
                canManage={canManage}
                onChanged={onChanged}
              />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

const settingsKey = (s: SupplierOverviewDto) =>
  [s.id, s.name, s.allowedModes.join(','), s.expectsConfirmation, s.defaultConfirmationDelayDays ?? ''].join('|')

function SettingsSection({
  supplier,
  canManage,
  manageHint,
  onChanged,
  onSavingChange,
}: {
  supplier: SupplierOverviewDto
  canManage: boolean
  manageHint?: string
  onChanged: () => void | Promise<void>
  onSavingChange: (saving: boolean) => void
}) {
  const [name, setName] = useState(supplier.name)
  const [modes, setModes] = useState<SupplierOrderMode[]>(supplier.allowedModes)
  const [expects, setExpects] = useState(supplier.expectsConfirmation)
  const [delay, setDelay] = useState(supplier.defaultConfirmationDelayDays?.toString() ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const dirty =
    name !== supplier.name ||
    expects !== supplier.expectsConfirmation ||
    delay !== (supplier.defaultConfirmationDelayDays?.toString() ?? '') ||
    modes.length !== supplier.allowedModes.length ||
    modes.some((m) => !supplier.allowedModes.includes(m))

  const save = async () => {
    const parsed = settingsSchema.safeParse({
      name,
      allowedModes: modes,
      expectsConfirmation: expects,
      defaultConfirmationDelayDays: expects && delay.trim() ? Number(delay) : null,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Réglages invalides.')
      return
    }
    setSaving(true)
    onSavingChange(true)
    try {
      await updateSupplier(supplier.id, parsed.data)
      setError(null)
      toast.success('Fournisseur enregistré')
      // Locked until the refreshed values arrive: they remount this form (keyed by them).
      await onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Le fournisseur n'a pas pu être enregistré.")
    } finally {
      setSaving(false)
      onSavingChange(false)
    }
  }

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">Commande</h3>
      <div className="space-y-1">
        <Label htmlFor="supplier-name">Nom</Label>
        <Input id="supplier-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage || saving} data-testid="supplier-sheet-name-input" />
      </div>
      <fieldset className="space-y-2" disabled={!canManage || saving}>
        <legend className="text-sm font-medium">Commandé par</legend>
        <div className="flex flex-wrap gap-4">
          {SUPPLIER_MODES.map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={modes.includes(m)}
                onCheckedChange={(checked) => setModes((prev) => (checked ? [...prev, m] : prev.filter((x) => x !== m)))}
                disabled={!canManage || saving}
                data-testid={`supplier-sheet-mode-${m.toLowerCase()}`}
              />
              {MODE_LABEL[m]}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={expects} onCheckedChange={(c) => setExpects(c === true)} disabled={!canManage || saving} data-testid="supplier-sheet-expects-confirmation" />
        Attend une confirmation de commande
      </label>
      {expects && (
        <div className="flex items-center gap-2 text-sm">
          <span>Délai de confirmation</span>
          <Input
            type="number"
            min={1}
            value={delay}
            onChange={(e) => setDelay(e.target.value)}
            placeholder="défaut"
            className="h-8 w-24"
            disabled={!canManage || saving}
            aria-label="Délai de confirmation en jours ouvrés"
            data-testid="supplier-sheet-delay-input"
          />
          <span className="text-muted-foreground">jours ouvrés</span>
        </div>
      )}
      {error && <p className="text-sm text-destructive" data-testid="supplier-sheet-settings-error">{error}</p>}
      <Button size="sm" onClick={save} disabled={!canManage || !dirty || saving} title={manageHint} data-testid="supplier-sheet-save-btn">
        Enregistrer
      </Button>
    </section>
  )
}

function BrandsSection({
  supplier,
  brands,
  canManage,
  onChanged,
}: {
  supplier: SupplierOverviewDto
  brands: BrandOverviewDto[]
  canManage: boolean
  onChanged: () => void
}) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // The field is locked while a brand is saved: clearing it on success must never wipe what the
  // user typed in the meantime (seen in QA: the second brand vanished).
  const add = async () => {
    const name = value.trim()
    if (!name || saving) return
    setSaving(true)
    try {
      await addSupplierBrand(supplier.id, name)
      setValue('')
      setError(null)
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "La marque n'a pas pu être ajoutée.")
    } finally {
      setSaving(false)
    }
  }

  const remove = async (brandId: string) => {
    try {
      await removeSupplierBrand(supplier.id, brandId)
      onChanged()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "La marque n'a pas pu être retirée.")
    }
  }

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Marques fournies</h3>
      {supplier.brands.length === 0 && <p className="text-sm text-muted-foreground">Aucune marque.</p>}
      <div className="flex flex-wrap gap-1">
        {supplier.brands.map((b) => (
          <Badge
            key={b.id}
            variant="outline"
            className={cn('gap-1 font-normal', isOwnBrand(supplier.name, b.name) && 'border-primary text-primary')}
          >
            {b.name}
            {canManage && (
              <button
                type="button"
                onClick={() => void remove(b.id)}
                className="rounded-full p-0.5 hover:bg-muted"
                aria-label={`Retirer ${b.name}`}
                data-testid="supplier-sheet-brand-remove-btn"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
      {canManage && (
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              setError(null)
            }}
            onKeyDown={(e) => e.key === 'Enter' && void add()}
            placeholder="Ajouter une marque"
            list="purchase-brand-names"
            className="h-8"
            disabled={saving}
            data-testid="supplier-sheet-brand-input"
          />
          <datalist id="purchase-brand-names">
            {brands.filter((b) => b.id).map((b) => (
              <option key={b.id} value={b.name} />
            ))}
          </datalist>
          <Button size="sm" variant="outline" onClick={() => void add()} disabled={!value.trim() || saving} data-testid="supplier-sheet-brand-add-btn">
            Ajouter
          </Button>
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </section>
  )
}

function AddressesSection({
  supplier,
  suppliers,
  platforms,
  canManage,
  onChanged,
}: {
  supplier: SupplierOverviewDto
  suppliers: SupplierOverviewDto[]
  platforms: OrderPlatformAddressDto[]
  canManage: boolean
  onChanged: () => void
}) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [owner, setOwner] = useState<SupplierOverviewDto | null>(null)
  const [saving, setSaving] = useState(false)

  // Locked while saving, like the brands field: success must not wipe a value typed meanwhile.
  const add = async () => {
    if (!value.trim() || saving) return
    setSaving(true)
    try {
      await addSupplierEmailAddress(supplier.id, value)
      setValue('')
      setError(null)
      setOwner(null)
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "L'adresse n'a pas pu être ajoutée.")
      const found = e instanceof ApiError && e.code === 'ADDRESS_ALREADY_OWNED' ? findAddressOwner(value, suppliers.filter((s) => s.id !== supplier.id), platforms) : null
      setOwner(found?.kind === 'supplier' && found.supplier.id !== supplier.id ? found.supplier : null)
    } finally {
      setSaving(false)
    }
  }

  // « Une adresse n'appartient qu'à un fournisseur » — the way forward is the brand, on the owner.
  const addBrandToOwner = async () => {
    if (!owner) return
    try {
      await addSupplierBrand(owner.id, supplier.name)
      toast.success(`Marque ${supplier.name} ajoutée à ${owner.name}`)
      setOwner(null)
      setError(null)
      setValue('')
      onChanged()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "La marque n'a pas pu être ajoutée.")
    }
  }

  const remove = async (addressId: string) => {
    try {
      await removeSupplierEmailAddress(supplier.id, addressId)
      onChanged()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "L'adresse n'a pas pu être retirée.")
    }
  }

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Adresses email</h3>
      {supplier.emailAddresses.length === 0 && <p className="text-sm text-muted-foreground">Aucune adresse.</p>}
      <ul className="space-y-1">
        {supplier.emailAddresses.map((a) => (
          <li key={a.id} className="flex items-center gap-2 text-sm" data-testid="supplier-sheet-address">
            <span className="font-mono text-xs">{a.value}</span>
            <span className="text-xs text-muted-foreground">
              {a.kind === 'Domain' ? 'domaine · ' : ''}
              {SOURCE_LABEL[a.source]}
            </span>
            {canManage && (
              <button
                type="button"
                onClick={() => void remove(a.id)}
                className="ml-auto rounded p-0.5 text-muted-foreground hover:bg-muted"
                aria-label={`Retirer ${a.value}`}
                data-testid="supplier-sheet-address-remove-btn"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canManage && (
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              setError(null)
              setOwner(null)
            }}
            onKeyDown={(e) => e.key === 'Enter' && void add()}
            placeholder="nom@domaine.be ou @domaine.be"
            className="h-8"
            disabled={saving}
            data-testid="supplier-sheet-address-input"
          />
          <Button size="sm" variant="outline" onClick={() => void add()} disabled={!value.trim() || saving} data-testid="supplier-sheet-address-add-btn">
            Ajouter
          </Button>
        </div>
      )}
      {error && (
        <div className="space-y-1">
          <p className="text-sm text-destructive" data-testid="supplier-sheet-address-error">{error}</p>
          {owner && (
            <Button size="sm" variant="outline" onClick={() => void addBrandToOwner()} data-testid="supplier-sheet-address-brand-to-owner-btn">
              Ajouter plutôt la marque {supplier.name} à {owner.name}
            </Button>
          )}
        </div>
      )}
    </section>
  )
}
